import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Skeleton from '@material-ui/lab/Skeleton';
import EditAccountInfoModal from './EditAccountInfoModal';
import { useProfile } from '../../../../../Contexts/UserProfileContext';
import { StyledButton } from '../../../../../Components/Generics.styled';
import useRequest from '../../../../../Hooks/useRequest';
import usePost from '../../../../../Hooks/usePost';
import { useAdvertisers } from '../../../../../Contexts/AdvertisersContext';
import { useAgency } from '../../../../../Contexts/AgencyContext';

const CompanyInfoApplet = styled.div`
    margin-top: 40px;
    border: 1px solid black;
    height: 100%;
    width: 100%;
    min-width: 600px;
    max-width: 800px;
    text-align: center;
    padding: 12px;
    background-color: #fafbf7;
    border-radius: 12px;
    opacity: 1;
`;

const CompanyInfoContainer = styled.div`
    display: flex;
    margin-left: auto;
    margin-right: auto;
    flex-direction: column;
`;

const Label = styled.div`
    padding: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #000000;
    text-align: left;
`;

const StyledValue = styled.span`
    letter-spacing: 0px;
    color: #000000;
    padding-left: 8px;
    font-weight: bold;
`;

const StyledHR = styled.hr`
    margin-top: 0px;
    margin-bottom: 0px;
`;

const Title = styled.div`
    font-size: 23px;
    font-weight: bold;
    margin-top: 80px;
    letter-spacing: 0px;
    color: #000000;
    opacity: 1;
    margin-left: 50px;
    text-align: center;
`;

const AccountInformation = () => {
    const { profile, setProfile, saveProfile, mutateProfile } = useProfile();
    const { setShouldFetchAdvertiserData, setShouldFetchOtherData } = useAdvertisers();
    const { setShouldFetchAgencyData } = useAgency();

    const profileIsEmpty = Object.keys(profile).length < 1;
    const { advertiser, agency, firstName, lastName, email, phoneNumber, isAgency } = {
        ...profile,
    };

    const { planTypeName, planTypeId, isBilledAnnually, stripeCustomerId } = {
        ...advertiser,
    };

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [productData, setProductData] = useState([]);
    const [currentPlanType, setCurrentPlanType] = useState({});
    const [postAccountData, setPostAccountData] = useState({
        success: null,
        error: null,
        isLoading: false,
    });

    const { data: allProductData } = useRequest('/stripe/getAllProducts');
    const { data: allPlansData } = useRequest('/getAllPlans');
    const { data: allPriceData } = useRequest('/stripe/getAllPrices');
    const { data: allCustomerSubscriptionsData } = useRequest(`/stripe/getSubscriptionsByCustomerId/${stripeCustomerId}`);
    const { post } = usePost('/stripe/updateSubscription');
    const { post: postUpdateAdvertiser } = usePost('/updateAdvertiser');
    const { post: postUpdateAgency } = usePost('/updateAgency');

    const currentSubscription = {
        planTypeName,
        planTypeId,
        duration: isBilledAnnually ? 'Annually' : 'Monthly',
        id: allCustomerSubscriptionsData?.data[0]?.id,
    };

    function areNamesSimilar(name1, name2) {
        // Remove spaces and compare in a case-insensitive manner
        const normalized1 = name1.replace(/\s+/g, '').toLowerCase();
        const normalized2 = name2.replace(/\s+/g, '').toLowerCase();

        return normalized1 === normalized2;
    }
    // Create a new array by combining data from both arrays based on similar names
    const combinedArray = allPlansData?.map((item1) => {
        const matchingItem = allProductData?.data?.find((item2) => areNamesSimilar(item1.name, item2.description));
        if (matchingItem) {
            return {
                ...item1,
                ...matchingItem,
            };
        }
        return item1; // If no matching item is found in array2, keep item from array1
    });

    const createProductData = () => {
        const productArray = [];
        if (combinedArray?.length > 0) {
            combinedArray?.map((product) => {
                allPriceData?.data?.map((price) => {
                    const productDescription = product?.description;
                    const priceNickname = price?.nickname?.split(' ')[0];
                    const priceDuration = price?.nickname?.split(' ')[1];

                    if (productDescription === priceNickname && priceDuration === 'Monthly') {
                        productArray.push({
                            name: product.name,
                            productId: product.id,
                            planTypeId: product.planTypeId,
                            fee: product.monthlyFee,
                            priceId: price.id,
                            duration: 'Monthly',
                        });
                    } else if (productDescription === priceNickname && priceDuration === 'Annually') {
                        productArray.push({
                            name: product.name,
                            productId: product.id,
                            planTypeId: product.planTypeId,
                            fee: product.annualFee,
                            priceId: price.id,
                            duration: 'Annually',
                        });
                    }
                });
            });
        }
        setProductData(productArray);
    };

    const setCurrentPlanMetaData = () => {
        const currentDuration = isBilledAnnually ? 'Annually' : 'Monthly';

        // Search for the current plan in productData based on name and duration
        const currentPlan = productData.find((product) => {
            return (
                ((product?.name === 'Agency Pro' && planTypeName === 'AgencyPro') || product?.name === planTypeName) &&
                currentDuration === product?.duration
            );
        });

        if (currentPlan) {
            setCurrentPlanType({
                name: currentPlan?.name,
                productId: currentPlan?.productId,
                planTypeId: currentPlan?.planTypeId,
                fee: currentPlan?.fee,
                priceId: currentPlan?.priceId,
                duration: currentPlan?.duration,
                current: true,
            });
        }
    };

    useEffect(() => {
        createProductData();
    }, [allPriceData, allPlansData]);

    useEffect(() => {
        setCurrentPlanMetaData();
    }, [productData, planTypeName]);

    const handlePlanTypeChange = (e, value) => {
        setCurrentPlanType({
            name: value.name,
            productId: value.productId,
            planTypeId: value.planTypeId,
            fee: value.fee,
            priceId: value.priceId,
            duration: value.duration,
        });
    };

    const checkIfPlanChanged = () => {
        if (currentPlanType.planTypeId === currentSubscription.planTypeId && currentPlanType.duration === currentSubscription.duration) {
            return false;
        } else return true;
    };
    const planHasChanged = checkIfPlanChanged();

    useEffect(() => {
        checkIfPlanChanged();
    }, [currentPlanType, currentSubscription]);

    const handleSave = async () => {
        try {
            setPostAccountData({
                ...postAccountData,
                success: false,
                error: false,
                isLoading: true,
            });
            await saveProfile(
                {
                    userId: profile.userId,
                    userName: profile?.email,
                    firstName: profile?.firstName,
                    lastName: profile?.lastName,
                    phoneNumber: profile?.phoneNumber,
                },
                'POST'
            );

            if (planHasChanged) {
                const body = {
                    customerId: stripeCustomerId,
                    newPlanPriceId: currentPlanType?.priceId,
                };
                // update subscription

                await post(body, 'POST');

                if (isAgency) {
                    const body = {
                        ...agency,
                        isBilledAnnually: currentPlanType?.duration === 'Annually' ? true : false,
                        planTypeId: currentPlanType?.planTypeId,
                        planTypeName: currentPlanType?.name,
                    };
                    await postUpdateAgency(body, 'POST');

                    setShouldFetchAgencyData(true);
                    // update agency plan
                } else {
                    //is individual update advertiser plan
                    const body = {
                        ...advertiser,
                        isBilledAnnually: currentPlanType?.duration === 'Annually' ? true : false,
                        planTypeId: currentPlanType?.planTypeId,
                        planTypeName: currentPlanType?.name,
                    };
                    await postUpdateAdvertiser(body, 'POST');
                }
                setShouldFetchAdvertiserData(true);
                setShouldFetchOtherData(true);
                // update db
            }

            await mutateProfile();
            setPostAccountData({ ...postAccountData, success: true, isLoading: false });
            setModalIsOpen(false);
        } catch (err) {
            setPostAccountData({
                ...postAccountData,
                error: true,
                success: false,
                isLoading: false,
            });

            console.log(err);
        }
    };
    return (
        <CompanyInfoContainer>
            <Title>Account Information</Title>
            <CompanyInfoApplet>
                {profileIsEmpty ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        First Name:
                        <StyledValue>{firstName}</StyledValue>{' '}
                    </Label>
                )}
                <StyledHR />
                {profileIsEmpty ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Last Name:
                        <StyledValue>{lastName}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {profileIsEmpty ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Email:
                        <StyledValue>{email}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {profileIsEmpty ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Phone Number:
                        <StyledValue>{phoneNumber}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {profileIsEmpty ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Account Type:
                        <StyledValue>{isAgency ? 'Agency' : 'Advertiser'}</StyledValue>
                    </Label>
                )}
                <StyledHR />
                {profileIsEmpty ? (
                    <Skeleton animation="wave" />
                ) : (
                    <Label>
                        Plan Type:
                        <StyledValue>{`${planTypeName} ${isBilledAnnually ? 'Annually' : 'Monthly'}`}</StyledValue>
                    </Label>
                )}

                <StyledHR />
                <br />
                <StyledButton onClick={() => setModalIsOpen(true)} variant="contained" color="primary">
                    Update
                </StyledButton>
                <EditAccountInfoModal
                    modalIsOpen={modalIsOpen}
                    setModalIsOpen={setModalIsOpen}
                    profile={profile}
                    postAccountData={postAccountData}
                    productData={productData}
                    handlePlanTypeChange={handlePlanTypeChange}
                    currentPlanType={currentPlanType}
                    setCurrentPlanMetaData={setCurrentPlanMetaData}
                    handleSave={handleSave}
                    setProfile={setProfile}
                    mutateProfile={mutateProfile}
                />
            </CompanyInfoApplet>
        </CompanyInfoContainer>
    );
};

export default AccountInformation;
